import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import useDebounce from "@nkr/utils/useDebounce";
import { Alert, Button, Col, DropdownButton, FormControl, Glyphicon, MenuItem, Modal, Row, Table, Well } from "react-bootstrap";
import { DashParentReferralSourceApi, IDashParentReferralSource, IDashReferralSource, DashReferralSourceApi, IDashDynamicRegPage, IDashGenericDynamicPage } from "@nkr/models/dash";
import { ActionCreators as AuthActionCreators } from "@nkr/store/authorization/actions";
import { useAppHookContext } from "@nkr/utils/rest-api";
import ReferralSourceSidebar from "./referral-source-sidebar";
import PageEditorPage from "./page-editor-page";
import { toast } from "react-toastify";

const emptyReferralSource = (): IDashReferralSource => ({
    id: 0,
    srcCode: "",
    description: "",
    parentReferralSourceId: null,
    parentReferralSourceName: "",
    isKeyReferrer: false,
    isNkrCampaign: false,
    overwriteToLegacyGenericPage: false,
    donorConnectGroup: null,
    donorConnectGroupName: null,
    activePageId: 0,
    activePageName: "",
    pageCount: 0,
});

type ISortKey = "description" | "parentReferralSourceName" | "srcCode" | "donorConnectGroupName" | "activePageId" | "overwriteToLegacyGenericPage";
type ISortDirection = "asc" | "desc";

interface IReferralSourceApiResponse<TBody> {
    status?: number,
    data?: TBody | string,
    response?: {
        status?: number,
        data?: TBody | string,
    }
};

interface IReferralSourceApiResponseBody {
    success?: boolean,
    payload?: IDashReferralSource,
    message?: string,
};

interface IReferralSourceSaveResult {
    updatedSource: IDashReferralSource | null,
    errorMessage: string,
};

interface IDynamicPageListApiResponseBody {
    success?: boolean,
    payload?: IDashDynamicRegPage[],
};

interface IGenericPageListApiResponseBody {
    success?: boolean,
    payload?: IDashGenericDynamicPage[],
};

const areDynamicPagesEqual = (left: IDashDynamicRegPage[], right: IDashDynamicRegPage[]) => {
    if (left === right) {
        return true;
    }

    if (left.length !== right.length) {
        return false;
    }

    return left.every((page, index) => {
        const other = right[index];
        return !!other
            && page.id === other.id
            && page.name === other.name
            && page.createdDate === other.createdDate
            && page.isActive === other.isActive;
    });
};

const DashReferralSources = () => {
    const parentResults = DashParentReferralSourceApi.useGetAll();
    const referralSourceResults = DashReferralSourceApi.useGetAll();
    const [selectedSource, setSelectedSource] = useState<IDashReferralSource | null>(null);
    const [parents, setParents] = useState<IDashParentReferralSource[]>([]);
    const [referralSources, setReferralSources] = useState<IDashReferralSource[]>([]);
    const [showAddParentModal, setShowAddParentModal] = useState(false);
    const [showDynamicPageModal, setShowDynamicPageModal] = useState(false);
    const [showGenericPageModal, setShowGenericPageModal] = useState(false);
    const [dynamicPageSource, setDynamicPageSource] = useState<IDashReferralSource | null>(null);
    const [selectedDynamicPageId, setSelectedDynamicPageId] = useState<number | null>(null);
    const [dynamicPages, setDynamicPages] = useState<IDashDynamicRegPage[]>([]);
    const [selectedGenericPageId, setSelectedGenericPageId] = useState<number | null>(null);
    const [genericPages, setGenericPages] = useState<IDashGenericDynamicPage[]>([]);
    const [newParentName, setNewParentName] = useState("");
    const [searchText, setSearchText] = useState("");
    const [sortKey, setSortKey] = useState<ISortKey>("description");
    const [sortDirection, setSortDirection] = useState<ISortDirection>("asc");
    const [pendingParentSelection, setPendingParentSelection] = useState<{ parent: IDashParentReferralSource, nonce: number } | null>(null);
    const [savingLegacyOverrideIds, setSavingLegacyOverrideIds] = useState<number[]>([]);
    const [isBulkUpdatingLegacyOverride, setIsBulkUpdatingLegacyOverride] = useState(false);
    const { dispatch } = useAppHookContext();
    const createParentHook = DashParentReferralSourceApi.useCreate();
    const shouldSelectInitialDynamicPageRef = useRef(false);
    const shouldSelectInitialGenericPageRef = useRef(false);

    useEffect(() => {
        setParents(parentResults?.payload ?? []);
    }, [parentResults?.payload]);

    useEffect(() => {
        setReferralSources(referralSourceResults?.payload ?? []);
    }, [referralSourceResults?.payload]);


    const currentSourceDynamicPages = useMemo(
        () => dynamicPageSource
            ? dynamicPages.filter((page) => page.entryReferrerId === dynamicPageSource.id)
            : [],
        [dynamicPageSource?.id, dynamicPages]
    );

    useEffect(() => {
        if (!showDynamicPageModal || !shouldSelectInitialDynamicPageRef.current || selectedDynamicPageId !== null || currentSourceDynamicPages.length === 0) {
            return;
        }

        const initialPage = currentSourceDynamicPages.find((page) => page.isActive) ?? currentSourceDynamicPages[0];
        shouldSelectInitialDynamicPageRef.current = false;
        setSelectedDynamicPageId(initialPage.id);
    }, [currentSourceDynamicPages, selectedDynamicPageId, showDynamicPageModal]);

    useEffect(() => {
        if (!showGenericPageModal || !shouldSelectInitialGenericPageRef.current || selectedGenericPageId !== null || genericPages.length === 0) {
            return;
        }

        const initialPage = genericPages.find((page) => page.isActive) ?? genericPages[0];
        shouldSelectInitialGenericPageRef.current = false;
        setSelectedGenericPageId(initialPage.id);
    }, [genericPages, selectedGenericPageId, showGenericPageModal]);

    const handleParentCreated = (parent: IDashParentReferralSource) => {
        setParents((current) => current.some((item) => item.id === parent.id) ? current : [...current, parent]);
    };

    const handleSort = (nextSortKey: ISortKey) => {
        if (sortKey === nextSortKey) {
            setSortDirection((current) => current === "asc" ? "desc" : "asc");
            return;
        }

        setSortKey(nextSortKey);
        setSortDirection("asc");
    };

    const filteredAndSortedSources = useMemo(() => {
        const normalizedSearch = searchText.trim().toLowerCase();
        const filtered = normalizedSearch
            ? referralSources.filter((source) => {
                const searchParts = [
                    source.description,
                    source.parentReferralSourceName,
                    source.srcCode,
                    source.donorConnectGroupName,
                    source.activePageId > 0 ? "active page set" : "add",
                    source.overwriteToLegacyGenericPage ? "legacy generic enabled" : "legacy generic disabled",
                ];

                return searchParts.some((part) => (part ?? "").toString().toLowerCase().includes(normalizedSearch));
            })
            : referralSources;

        const sorted = [...filtered].sort((left, right) => {
            let leftValue: string | number = "";
            let rightValue: string | number = "";

            if (sortKey === "activePageId") {
                leftValue = left.activePageId > 0 ? 1 : 0;
                rightValue = right.activePageId > 0 ? 1 : 0;
            } else if (sortKey === "overwriteToLegacyGenericPage") {
                leftValue = left.overwriteToLegacyGenericPage ? 1 : 0;
                rightValue = right.overwriteToLegacyGenericPage ? 1 : 0;
            } else {
                leftValue = (left[sortKey] ?? "").toString().toLowerCase();
                rightValue = (right[sortKey] ?? "").toString().toLowerCase();
            }

            if (leftValue < rightValue) {
                return sortDirection === "asc" ? -1 : 1;
            }

            if (leftValue > rightValue) {
                return sortDirection === "asc" ? 1 : -1;
            }

            return 0;
        });

        return sorted;
    }, [referralSources, searchText, sortDirection, sortKey]);

    const isParentNameAvailable = (name: string) =>
        !parents.some(existing => existing.name.toLowerCase() === name.trim().toLowerCase());

    const parentValidation = useDebounce(newParentName, 500, isParentNameAvailable) as { debouncedValue: string; isValid: boolean; isValidating: boolean };

    const closeAddParentModal = () => {
        setShowAddParentModal(false);
        setNewParentName("");
    };

    const closeDynamicPageModal = () => {
        setShowDynamicPageModal(false);
        setDynamicPageSource(null);
        setDynamicPages([]);
        setSelectedDynamicPageId(null);
        shouldSelectInitialDynamicPageRef.current = false;
    };

    const closeGenericPageModal = () => {
        setShowGenericPageModal(false);
        setGenericPages([]);
        setSelectedGenericPageId(null);
        shouldSelectInitialGenericPageRef.current = false;
    };

    const handleAddParent = async () => {
        const name = newParentName.trim();

        if (!name || !isParentNameAvailable(name)) return;

        const result = await createParentHook.submit({ id: 0, name });

        if (!result?.isSuccess || !result.payload) return;

        const newParent = result.payload;

        handleParentCreated(newParent);

        if (selectedSource) {
            setPendingParentSelection({ parent: newParent, nonce: Date.now() });
        }

        closeAddParentModal();
    };

    const getSortIndicator = (columnKey: ISortKey) => {

        if (sortKey !== columnKey) {
            return "";
        }

        return "glyphicon" + (sortDirection === "asc" ? " glyphicon-triangle-top" : " glyphicon-triangle-bottom");
    };

    const syncReferralSourceState = (sourceId: number, updater: (source: IDashReferralSource) => IDashReferralSource) => {
        setReferralSources((current) => current.map((source) => source.id === sourceId ? updater(source) : source));
        setSelectedSource((current) => current && current.id === sourceId ? updater(current) : current);
        setDynamicPageSource((current) => current && current.id === sourceId ? updater(current) : current);
    };

    const buildReferralSourcePayload = (source: IDashReferralSource, overwriteToLegacyGenericPage: boolean): IDashReferralSource => ({
        ...source,
        overwriteToLegacyGenericPage,
    });

    const setLegacyOverrideSaving = (sourceId: number, isSaving: boolean) => {
        setSavingLegacyOverrideIds((current) => isSaving
            ? current.includes(sourceId) ? current : [...current, sourceId]
            : current.filter((id) => id !== sourceId));
    };

    const getResponseBody = <TBody,>(response: IReferralSourceApiResponse<TBody>): TBody | null => {
        const rootData = response?.data;
        if (rootData && typeof rootData === "object") {
            return rootData as TBody;
        }

        const nestedData = response?.response?.data;
        if (nestedData && typeof nestedData === "object") {
            return nestedData as TBody;
        }

        return null;
    };

    const getReferralSourceErrorMessage = (response: IReferralSourceApiResponse<IReferralSourceApiResponseBody>, fallbackMessage: string): string => {
        const data = getResponseBody(response);
        if (data?.message) {
            return data.message ?? fallbackMessage;
        }

        if (typeof response?.response?.data === "string" && response.response.data.trim()) {
            return response.response.data;
        }

        if (typeof response?.data === "string" && response.data.trim()) {
            return response.data;
        }

        return fallbackMessage;
    };

    const loadDynamicPagesForSource = async (sourceId: number) => {
        const response = await dispatch(AuthActionCreators.apiGet({
            url: `${__API__}/dash/referral-sources/${sourceId}/pages`,
        })) as IReferralSourceApiResponse<IDynamicPageListApiResponseBody>;
        const responseBody = getResponseBody(response);
        const nextPages = responseBody?.payload ?? [];
        const normalizedPages = Array.isArray(nextPages) ? nextPages : [];

        setDynamicPages((current) => areDynamicPagesEqual(current, normalizedPages) ? current : normalizedPages);
        return normalizedPages;
    };

    const loadGenericPages = async () => {
        const response = await dispatch(AuthActionCreators.apiGet({
            url: `${__API__}/dash/generic-pages`,
        })) as IReferralSourceApiResponse<IGenericPageListApiResponseBody>;
        const responseBody = getResponseBody(response);
        const nextPages = responseBody?.payload ?? [];
        const normalizedPages = Array.isArray(nextPages) ? nextPages : [];

        setGenericPages((current) => areDynamicPagesEqual(current, normalizedPages) ? current : normalizedPages);
        return normalizedPages;
    };

    const saveReferralSourceLegacyOverride = async (source: IDashReferralSource, overwriteToLegacyGenericPage: boolean): Promise<IReferralSourceSaveResult> => {
        const payload = buildReferralSourcePayload(source, overwriteToLegacyGenericPage);
        const response = await dispatch(AuthActionCreators.apiPut({
            url: `${__API__}/dash/referral-sources/${source.id}`,
            data: payload,
        })) as IReferralSourceApiResponse<IReferralSourceApiResponseBody>;
        const data = getResponseBody(response);

        return {
            updatedSource: data?.payload ?? null,
            errorMessage: getReferralSourceErrorMessage(response, "Unable to update this referral source."),
        };
    };

    const handleLegacyOverrideChanged = async (source: IDashReferralSource, overwriteToLegacyGenericPage: boolean) => {
        if (source.id <= 0 || savingLegacyOverrideIds.includes(source.id) || isBulkUpdatingLegacyOverride) {
            return;
        }

        setLegacyOverrideSaving(source.id, true);
        try {
            const saveResult = await saveReferralSourceLegacyOverride(source, overwriteToLegacyGenericPage);
            if (!saveResult.updatedSource) {
                toast.error(saveResult.errorMessage);
                return;
            }

            syncReferralSourceState(source.id, () => saveResult.updatedSource as IDashReferralSource);
        } finally {
            setLegacyOverrideSaving(source.id, false);
        }
    };

    const handleBulkLegacyOverrideChanged = async (overwriteToLegacyGenericPage: boolean) => {
        if (isBulkUpdatingLegacyOverride) {
            return;
        }

        const sourcesToUpdate = referralSources.filter((source) => source.id > 0 && source.overwriteToLegacyGenericPage !== overwriteToLegacyGenericPage);
        if (sourcesToUpdate.length === 0) {
            return;
        }

        setIsBulkUpdatingLegacyOverride(true);
        setSavingLegacyOverrideIds((current) => Array.from(new Set([...current, ...sourcesToUpdate.map((source) => source.id)])));

        try {
            const results = await Promise.all(sourcesToUpdate.map(async (source) => ({
                sourceId: source.id,
                result: await saveReferralSourceLegacyOverride(source, overwriteToLegacyGenericPage),
            })));

            const succeeded = results.filter((result) => !!result.result.updatedSource);
            const failed = results.filter((result) => !result.result.updatedSource);
            const failedCount = failed.length;

            succeeded.forEach((result) => {
                syncReferralSourceState(result.sourceId, () => result.result.updatedSource as IDashReferralSource);
            });

            if (failedCount > 0) {
                const failedMessage = failedCount === 1
                    ? failed[0].result.errorMessage
                    : `Unable to update ${failedCount} referral sources.`;
                toast.error(failedMessage);
            }
        } finally {
            setSavingLegacyOverrideIds((current) => current.filter((id) => !sourcesToUpdate.some((source) => source.id === id)));
            setIsBulkUpdatingLegacyOverride(false);
        }
    };

    const openDynamicPageModal = (source: IDashReferralSource, pageId?: number | null) => {
        setDynamicPageSource(source);
        setDynamicPages([]);
        setSelectedDynamicPageId(pageId ?? null);
        shouldSelectInitialDynamicPageRef.current = pageId == null;
        setShowDynamicPageModal(true);
        void loadDynamicPagesForSource(source.id);
    };

    const openGenericPageModal = (pageId?: number | null) => {
        setGenericPages([]);
        setSelectedGenericPageId(pageId ?? null);
        shouldSelectInitialGenericPageRef.current = pageId == null;
        setShowGenericPageModal(true);
        void loadGenericPages();
    };

    const scrollDynamicPageSectionIntoView = (sectionId: string) => {
        window.requestAnimationFrame(() => {
            document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    };

    const selectDynamicPage = (pageId: number, sectionId?: string) => {
        setSelectedDynamicPageId(pageId);
        if (sectionId) {
            scrollDynamicPageSectionIntoView(sectionId);
        }
    };

    const handleDynamicPageSaved = (page: IDashDynamicRegPage, isNewPage: boolean) => {
        if (!dynamicPageSource) {
            return;
        }

        setDynamicPages((current) => {
            const nextPages = current.some((item) => item.id === page.id)
                ? current.map((item) => item.id === page.id ? page : item)
                : [...current, page];

            if (!page.isActive) {
                return nextPages;
            }

            return nextPages.map((item) => ({
                ...item,
                isActive: item.id === page.id,
            }));
        });

        syncReferralSourceState(dynamicPageSource.id, (source) => {
            const nextPageCount = isNewPage ? source.pageCount + 1 : source.pageCount;

            if (page.isActive) {
                return {
                    ...source,
                    activePageId: page.id,
                    activePageName: page.name,
                    pageCount: nextPageCount,
                };
            }

            return {
                ...source,
                activePageId: source.activePageId === page.id ? 0 : source.activePageId,
                activePageName: source.activePageId === page.id ? "" : source.activePageName,
                pageCount: nextPageCount,
            };
        });

        setSelectedDynamicPageId(page.id);
    };

    const handleDynamicPageDeleted = (deletedPageId: number, wasActive: boolean) => {
        if (!dynamicPageSource) {
            return;
        }

        const remainingPages = dynamicPages.filter((page) => page.id !== deletedPageId);
        const nextSelectedPageId = remainingPages.length > 0 ? remainingPages[0].id : null;

        setDynamicPages(remainingPages);

        syncReferralSourceState(dynamicPageSource.id, (source) => ({
            ...source,
            activePageId: wasActive ? 0 : source.activePageId,
            activePageName: wasActive ? "" : source.activePageName,
            pageCount: Math.max(0, source.pageCount - 1),
        }));

        setSelectedDynamicPageId(nextSelectedPageId);
    };

    const handleGenericPageSaved = (page: IDashGenericDynamicPage) => {
        setGenericPages((current) => {
            const nextPages = current.some((item) => item.id === page.id)
                ? current.map((item) => item.id === page.id ? page : item)
                : [...current, page];

            if (!page.isActive) {
                return nextPages;
            }

            return nextPages.map((item) => ({
                ...item,
                isActive: item.id === page.id,
            }));
        });

        setSelectedGenericPageId(page.id);
    };

    const handleGenericPageDeleted = (deletedPageId: number) => {
        const remainingPages = genericPages.filter((page) => page.id !== deletedPageId);
        const nextSelectedPageId = remainingPages.length > 0 ? remainingPages[0].id : null;

        setGenericPages(remainingPages);
        setSelectedGenericPageId(nextSelectedPageId);
    };

    const activateDynamicPage = async (page: IDashDynamicRegPage) => {
        if (!dynamicPageSource || page.isActive) {
            return;
        }

        const response = await dispatch(AuthActionCreators.apiPost({
            url: `${__API__}/dash/referral-sources/pages/${page.id}/activate`,
            data: {},
        })) as { data?: { Success?: boolean, Payload?: IDashDynamicRegPage } };

        const activatedPage = response?.data?.Payload ?? page;
        setDynamicPages((current) => current.map((item) => ({
            ...item,
            ...(item.id === activatedPage.id ? activatedPage : {}),
            isActive: item.id === activatedPage.id,
        })));

        syncReferralSourceState(dynamicPageSource.id, (source) => ({
            ...source,
            activePageId: activatedPage.id,
            activePageName: activatedPage.name,
        }));

        setSelectedDynamicPageId(activatedPage.id);
        await loadDynamicPagesForSource(dynamicPageSource.id);
    };

    const deactivateDynamicPage = async (page: IDashDynamicRegPage) => {
        if (!dynamicPageSource || !page.isActive) {
            return;
        }

        const response = await dispatch(AuthActionCreators.apiPost({
            url: `${__API__}/dash/referral-sources/pages/${page.id}/deactivate`,
            data: {},
        })) as { data?: { Success?: boolean, Payload?: IDashDynamicRegPage } };

        const deactivatedPage = response?.data?.Payload ?? { ...page, isActive: false };
        setDynamicPages((current) => current.map((item) => item.id === deactivatedPage.id
            ? { ...item, ...deactivatedPage, isActive: false }
            : item));

        syncReferralSourceState(dynamicPageSource.id, (source) => ({
            ...source,
            activePageId: source.activePageId === deactivatedPage.id ? 0 : source.activePageId,
            activePageName: source.activePageId === deactivatedPage.id ? "" : source.activePageName,
        }));

        setSelectedDynamicPageId(deactivatedPage.id);
        await loadDynamicPagesForSource(dynamicPageSource.id);
    };

    const deleteDynamicPage = async (page: IDashDynamicRegPage) => {
        if (!dynamicPageSource) {
            return;
        }

        if (!window.confirm(`Delete "${page.name}"?`)) {
            return;
        }

        const response = await dispatch(AuthActionCreators.apiDelete({
            url: `${__API__}/dash/referral-sources/pages/${page.id}`,
        })) as { status?: number, data?: { success?: boolean, isSuccess?: boolean } };

        const deleteSucceeded = response?.status === 200 || response?.data?.success || response?.data?.isSuccess;
        if (!deleteSucceeded) {
            toast.error("Unable to delete this page.");
            return;
        }

        handleDynamicPageDeleted(page.id, page.isActive);
        await loadDynamicPagesForSource(dynamicPageSource.id);
    };

    const activateGenericPage = async (page: IDashGenericDynamicPage) => {
        if (page.isActive) {
            return;
        }

        const response = await dispatch(AuthActionCreators.apiPost({
            url: `${__API__}/dash/generic-pages/${page.id}/activate`,
            data: {},
        })) as { data?: { Success?: boolean, Payload?: IDashGenericDynamicPage } };

        const activatedPage = response?.data?.Payload ?? page;
        setGenericPages((current) => current.map((item) => ({
            ...item,
            ...(item.id === activatedPage.id ? activatedPage : {}),
            isActive: item.id === activatedPage.id,
        })));

        setSelectedGenericPageId(activatedPage.id);
        await loadGenericPages();
    };

    const deactivateGenericPage = async (page: IDashGenericDynamicPage) => {
        if (!page.isActive) {
            return;
        }

        const response = await dispatch(AuthActionCreators.apiPost({
            url: `${__API__}/dash/generic-pages/${page.id}/deactivate`,
            data: {},
        })) as { data?: { Success?: boolean, Payload?: IDashGenericDynamicPage } };

        const deactivatedPage = response?.data?.Payload ?? { ...page, isActive: false };
        setGenericPages((current) => current.map((item) => item.id === deactivatedPage.id
            ? { ...item, ...deactivatedPage, isActive: false }
            : item));

        setSelectedGenericPageId(deactivatedPage.id);
        await loadGenericPages();
    };

    const deleteGenericPage = async (page: IDashGenericDynamicPage) => {
        if (!window.confirm(`Delete "${page.name}"?`)) {
            return;
        }

        const response = await dispatch(AuthActionCreators.apiDelete({
            url: `${__API__}/dash/generic-pages/${page.id}`,
        })) as { status?: number, data?: { success?: boolean, isSuccess?: boolean } };

        const deleteSucceeded = response?.status === 200 || response?.data?.success || response?.data?.isSuccess;
        if (!deleteSucceeded) {
            toast.error("Unable to delete this page.");
            return;
        }

        handleGenericPageDeleted(page.id);
        await loadGenericPages();
    };

    const selectedDynamicPage = selectedDynamicPageId == null
        ? null
        : currentSourceDynamicPages.find((page) => page.id === selectedDynamicPageId) ?? null;

    const selectedGenericPage = selectedGenericPageId == null
        ? null
        : genericPages.find((page) => page.id === selectedGenericPageId) ?? null;

    function getDdrpLink(source: IDashReferralSource): import("react").ReactNode {
        if (!source.description || !source.srcCode) {
            return source.description || "--";
        }

        return (
            <a
                href={`https://nkr.donorscreen${__HIGH_LEVEL_DASH_DOMAIN__}/register/now?src=${encodeURIComponent(source.srcCode)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
            >
                {source.description}
            </a>
        );
    }

    function getActivePageLink(source: IDashReferralSource): import("react").ReactNode {
        if (source.overwriteToLegacyGenericPage) {
            return "DDRP overwrite";
        }

        let label: string;
        if (source.activePageId > 0) {
            label = source.activePageName;
        } else if (source.pageCount > 0) {
            label = `${source.pageCount} inactive page${source.pageCount === 1 ? "" : "s"}`;
        } else {
            label = "+ Add";
        }

        return (
            <a
                href="#"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openDynamicPageModal(source, source.activePageId > 0 ? source.activePageId : null);
                }}
            >
                {label}
            </a>
        );
    }

    return (
        <>
            <Row style={{ margin: "12px 0" }}>
                <Col xs={6}>
                    <h3 style={{ margin: 0 }}>DASH Referral Sources</h3>
                </Col>
                <Col xs={6} style={{ textAlign: "right" }}>
                    <Button style={{ marginRight: 8 }} onClick={() => setShowAddParentModal(true)}>
                        <Glyphicon glyph="plus" /> Add new parent
                    </Button>                    
                    <Button bsStyle="primary" onClick={() => setSelectedSource(emptyReferralSource())}>
                        <Glyphicon glyph="plus" /> Add Referral Source
                    </Button>
                    <Button style={{ marginLeft: 8 }} onClick={() => openGenericPageModal()}>
                        Manage Generic Landing Page
                    </Button>
                </Col>
            </Row>

            <Well>
                <Row style={{ marginBottom: 12 }}>
                    <Col xs={12} md={5}>
                        <FormControl
                            type="text"
                            placeholder="Search by name, parent SRC, child SRC, page status, or legacy override..."
                            value={searchText}
                            onChange={(e: FormEvent<FormControl & HTMLInputElement>) => setSearchText(e.currentTarget.value)}
                        />
                    </Col>
                    <Col xs={12} md={7} style={{ textAlign: "right" }}>
                        <Button
                            style={{ marginRight: 8, marginTop: 8 }}
                            onClick={() => void handleBulkLegacyOverrideChanged(true)}
                            disabled={isBulkUpdatingLegacyOverride}
                        >
                            Enable All Legacy Overrides
                        </Button>
                        <Button
                            style={{ marginTop: 8 }}
                            onClick={() => void handleBulkLegacyOverrideChanged(false)}
                            disabled={isBulkUpdatingLegacyOverride}
                        >
                            Disable All Legacy Overrides
                        </Button>
                    </Col>
                </Row>
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th><button type="button" className="btn btn-link btn-xs" style={{ padding: 0, fontWeight: "bold" }} onClick={() => handleSort("description")}>Name <span className={getSortIndicator("description")}></span></button></th>
                            <th><button type="button" className="btn btn-link btn-xs" style={{ padding: 0, fontWeight: "bold" }} onClick={() => handleSort("parentReferralSourceName")}>Parent SRC <span className={getSortIndicator("parentReferralSourceName")}></span></button></th>
                            <th><button type="button" className="btn btn-link btn-xs" style={{ padding: 0, fontWeight: "bold" }} onClick={() => handleSort("srcCode")}>Child SRC <span className={getSortIndicator("srcCode")}></span></button></th>
                            <th><button type="button" className="btn btn-link btn-xs" style={{ padding: 0, fontWeight: "bold" }} onClick={() => handleSort("donorConnectGroupName")}>Mentor Group <span className={getSortIndicator("donorConnectGroupName")}></span></button></th>
                            <th><button type="button" className="btn btn-link btn-xs" style={{ padding: 0, fontWeight: "bold" }} onClick={() => handleSort("activePageId")}>Dynamic Reg Page <span className={getSortIndicator("activePageId")}></span></button></th>
                            <th><button type="button" className="btn btn-link btn-xs" style={{ padding: 0, fontWeight: "bold" }} onClick={() => handleSort("overwriteToLegacyGenericPage")}>Overwrite to Legacy Generic Page <span className={getSortIndicator("overwriteToLegacyGenericPage")}></span></button></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredAndSortedSources.length === 0 && (
                            <tr>
                                <td colSpan={6}>No referral sources found.</td>
                            </tr>
                        )}
                        {filteredAndSortedSources.map((source) => (
                            <tr key={String(source.id)} style={{ cursor: "pointer" }} onClick={() => setSelectedSource(source)}>
                                <td>{getDdrpLink(source)}</td>
                                <td>{source.parentReferralSourceName || "--"}</td>
                                <td>{source.srcCode}</td>
                                <td>{source.donorConnectGroupName}</td>
                                <td>{getActivePageLink(source)}</td>
                                <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                    <input
                                        type="checkbox"
                                        checked={source.overwriteToLegacyGenericPage}
                                        disabled={isBulkUpdatingLegacyOverride || savingLegacyOverrideIds.includes(source.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            void handleLegacyOverrideChanged(source, e.currentTarget.checked);
                                        }}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Well>

            {selectedSource && (
                <ReferralSourceSidebar
                    parents={parents}
                    sourceCodes={referralSources.map((item) => item.srcCode).filter((item) => !!item)}
                    selectedSource={selectedSource}
                    setSelectedSource={setSelectedSource}
                    pendingParentSelection={pendingParentSelection}
                />
            )}

            <Modal
                show={showDynamicPageModal}
                onHide={closeDynamicPageModal}
                backdrop="static"
                bsSize="large"
                dialogClassName="dash-dynamic-page-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Dynamic Donor Registration Page</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "85vh", overflowY: "auto" }}>
                    {dynamicPageSource && (
                        <>
                            <Row style={{ marginBottom: 12 }}>
                                <Col xs={12} md={8}>
                                    <strong>Referral Source</strong>
                                    <div>{dynamicPageSource.description || "--"}</div>
                                </Col>
                                <Col xs={12} md={4} style={{ textAlign: "right" }}>
                                    <Button bsStyle="success" onClick={() => {
                                        shouldSelectInitialDynamicPageRef.current = false;
                                        setSelectedDynamicPageId(null);
                                    }}>
                                        <Glyphicon glyph="plus" /> Add Page
                                    </Button>
                                </Col>
                            </Row>

                            <Table bordered hover condensed>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Created</th>
                                        <th>Active</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentSourceDynamicPages.length === 0 && (
                                        <tr>
                                            <td colSpan={4}>No versions yet.</td>
                                        </tr>
                                    )}
                                    {currentSourceDynamicPages.map((page) => (
                                        <tr
                                            key={String(page.id)}
                                            style={{ cursor: "pointer", backgroundColor: selectedDynamicPageId === page.id ? "#f5f5f5" : undefined }}
                                            onClick={() => setSelectedDynamicPageId(page.id)}
                                        >
                                            <td>{page.name}</td>
                                            <td>{page.createdDate || "--"}</td>
                                            <td>{page.isActive ? "Yes" : "No"}</td>
                                            <td>
                                                <DropdownButton
                                                    id={`dash-dynamic-page-actions-${page.id}`}
                                                    title="Actions"
                                                    bsStyle="primary"
                                                    bsSize="xsmall"
                                                    onClick={(e: any) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }}
                                                >
                                                    <MenuItem
                                                        onClick={(e: any) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            selectDynamicPage(page.id, "dash-dynamic-page-preview");
                                                        }}
                                                    >
                                                        Preview
                                                    </MenuItem>
                                                    <MenuItem
                                                        onClick={(e: any) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            selectDynamicPage(page.id, "dash-dynamic-page-editor");
                                                        }}
                                                    >
                                                        Edit
                                                    </MenuItem>
                                                    <MenuItem
                                                        onClick={(e: any) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            void deleteDynamicPage(page);
                                                        }}
                                                    >
                                                        Delete
                                                    </MenuItem>
                                                    <MenuItem
                                                        disabled={!page.isActive}
                                                        onClick={(e: any) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            void deactivateDynamicPage(page);
                                                        }}
                                                    >
                                                        Deactivate
                                                    </MenuItem>
                                                    <MenuItem
                                                        disabled={page.isActive}
                                                        onClick={(e: any) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            void activateDynamicPage(page);
                                                        }}
                                                    >
                                                        Activate
                                                    </MenuItem>
                                                </DropdownButton>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            <PageEditorPage
                                key={`${dynamicPageSource.id}-${selectedDynamicPageId ?? "new"}`}
                                embedded={true}
                                mode="source"
                                sourceId={dynamicPageSource.id}
                                pageId={selectedDynamicPageId ?? undefined}
                                onSaved={handleDynamicPageSaved}
                                onDeleted={handleDynamicPageDeleted}
                                currentPageData={selectedDynamicPage}
                            />
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={closeDynamicPageModal}>Close</Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showGenericPageModal}
                onHide={closeGenericPageModal}
                backdrop="static"
                bsSize="large"
                dialogClassName="dash-dynamic-page-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Generic Dynamic Landing Page</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "85vh", overflowY: "auto" }}>
                    <Row style={{ marginBottom: 12 }}>
                        <Col xs={12} md={8}>
                            <strong>Fallback Behavior</strong>
                            <div>Shown when a donor visits with a src value that does not have an active source-specific page.</div>
                        </Col>
                        <Col xs={12} md={4} style={{ textAlign: "right" }}>
                            <Button bsStyle="success" onClick={() => {
                                shouldSelectInitialGenericPageRef.current = false;
                                setSelectedGenericPageId(null);
                            }}>
                                <Glyphicon glyph="plus" /> Add Page
                            </Button>
                        </Col>
                    </Row>

                    <Table bordered hover condensed>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Created</th>
                                <th>Active</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {genericPages.length === 0 && (
                                <tr>
                                    <td colSpan={4}>No versions yet.</td>
                                </tr>
                            )}
                            {genericPages.map((page) => (
                                <tr
                                    key={String(page.id)}
                                    style={{ cursor: "pointer", backgroundColor: selectedGenericPageId === page.id ? "#f5f5f5" : undefined }}
                                    onClick={() => setSelectedGenericPageId(page.id)}
                                >
                                    <td>{page.name}</td>
                                    <td>{page.createdDate || "--"}</td>
                                    <td>{page.isActive ? "Yes" : "No"}</td>
                                    <td>
                                        <DropdownButton
                                            id={`dash-generic-page-actions-${page.id}`}
                                            title="Actions"
                                            bsStyle="primary"
                                            bsSize="xsmall"
                                            onClick={(e: any) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                        >
                                            <MenuItem
                                                onClick={(e: any) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setSelectedGenericPageId(page.id);
                                                    scrollDynamicPageSectionIntoView("dash-dynamic-page-preview");
                                                }}
                                            >
                                                Preview
                                            </MenuItem>
                                            <MenuItem
                                                onClick={(e: any) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setSelectedGenericPageId(page.id);
                                                    scrollDynamicPageSectionIntoView("dash-dynamic-page-editor");
                                                }}
                                            >
                                                Edit
                                            </MenuItem>
                                            <MenuItem
                                                onClick={(e: any) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    void deleteGenericPage(page);
                                                }}
                                            >
                                                Delete
                                            </MenuItem>
                                            <MenuItem
                                                disabled={!page.isActive}
                                                onClick={(e: any) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    void deactivateGenericPage(page);
                                                }}
                                            >
                                                Deactivate
                                            </MenuItem>
                                            <MenuItem
                                                disabled={page.isActive}
                                                onClick={(e: any) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    void activateGenericPage(page);
                                                }}
                                            >
                                                Activate
                                            </MenuItem>
                                        </DropdownButton>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    <PageEditorPage
                        key={`generic-${selectedGenericPageId ?? "new"}`}
                        embedded={true}
                        mode="generic"
                        pageId={selectedGenericPageId ?? undefined}
                        onSaved={(page) => handleGenericPageSaved(page as IDashGenericDynamicPage)}
                        onDeleted={handleGenericPageDeleted}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={closeGenericPageModal}>Close</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showAddParentModal} backdrop="static" onHide={closeAddParentModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Add new parent</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {newParentName.trim() && !parentValidation.isValid && (
                        <Alert bsStyle="warning">A parent with this name already exists.</Alert>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <FormControl
                            type="text"
                            placeholder="Parent referral source name"
                            value={newParentName}
                            onChange={(e: FormEvent<FormControl & HTMLInputElement>) => setNewParentName(e.currentTarget.value)}
                            style={{ borderColor: newParentName.trim() && !parentValidation.isValid ? "#a94442" : undefined }}
                        />
                        {newParentName.trim() && !parentValidation.isValid && (
                            <Glyphicon glyph="exclamation-sign" style={{ color: "#a94442", flexShrink: 0 }} />
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={closeAddParentModal}>Cancel</Button>
                    <Button
                        bsStyle="primary"
                        onClick={handleAddParent}
                        disabled={!newParentName.trim() || !parentValidation.isValid || parentValidation.isValidating || createParentHook.isMutating}
                    >
                        Add Parent
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default DashReferralSources;